/**
 * Typed-rejection parsing for booking commands (DOMAIN.md §C.6 `AvailabilityRejection`, §D.1). The
 * backend returns business failures as RFC 9457 problems carrying a STABLE `rejectionCode` (branch on
 * this, never on HTTP status) plus `alternatives` (always present, may be empty) and, for fit
 * failures, `largestFitParty`. This turns a thrown `ApiError` into that shape without body-sniffing
 * at the call site — so manual-entry / edit render clickable alternatives, the diner-equivalent
 * lead-time/horizon message, and the blacklist BLOCK stop, from one place (R2 / R5).
 */
import { apiProblemOf } from "@repo/api-client";

export interface AltSlot {
  startSec: number;
  durationSec: number;
}

export interface BookingRejection {
  rejectionCode: string;
  alternatives: AltSlot[];
  largestFitParty?: number;
}

/** Rejections that carry alternative slots the staff can click to retry (R2 slot-just-taken). */
const WITH_ALTERNATIVES = new Set(["SLOT_UNAVAILABLE", "COVERS_CAP_REACHED"]);

/** Policy blocks a MANAGER may override with a reason (LeadTimeViolated / HorizonExceeded). */
const OVERRIDABLE = new Set(["LEAD_TIME_VIOLATED", "HORIZON_EXCEEDED"]);

export function bookingRejectionOf(err: unknown): BookingRejection | undefined {
  const problem = apiProblemOf(err) as
    | {
        rejectionCode?: string;
        alternatives?: Array<{ startSec?: number; durationSec?: number }>;
        largestFitParty?: number;
      }
    | undefined;
  const code = problem?.rejectionCode;
  if (!code) return undefined;
  return {
    rejectionCode: code,
    alternatives: (problem.alternatives ?? [])
      .filter((a) => typeof a.startSec === "number" && typeof a.durationSec === "number")
      .map((a) => ({ startSec: a.startSec as number, durationSec: a.durationSec as number })),
    largestFitParty:
      typeof problem.largestFitParty === "number" ? problem.largestFitParty : undefined,
  };
}

export function hasAlternatives(rej: BookingRejection): boolean {
  return WITH_ALTERNATIVES.has(rej.rejectionCode) && rej.alternatives.length > 0;
}

export function isOverridable(rej: BookingRejection): boolean {
  return OVERRIDABLE.has(rej.rejectionCode);
}

export function isBlocked(rej: BookingRejection): boolean {
  return rej.rejectionCode === "GUEST_BLOCKED";
}

/** i18n key for a rejection message (falls back to a generic key for unmodelled codes). */
export function rejectionMessageKey(rej: BookingRejection): string {
  const known = new Set([
    "SLOT_UNAVAILABLE",
    "COVERS_CAP_REACHED",
    "NO_FIT_FOR_PARTY",
    "OVERLAPPING_RESERVATION",
    "OUTSIDE_OPENING_HOURS",
    "LEAD_TIME_VIOLATED",
    "HORIZON_EXCEEDED",
    "ONLINE_BOOKING_DISABLED",
    "VENUE_NOT_LIVE",
    "GUEST_BLOCKED",
    "UNMAPPED_CHANNEL",
    "LARGE_PARTY_REDIRECT",
    "NOT_ACTIONABLE",
    "SEATED_NOT_CANCELLABLE",
    "OVERRIDE_NOT_PERMITTED",
  ]);
  return known.has(rej.rejectionCode)
    ? `bookings.reject.${rej.rejectionCode}`
    : "bookings.reject.generic";
}
